/**
 * BuyElectro wishlist.
 *
 * Saved in this browser's localStorage, so it survives refreshes, closed tabs and restarts.
 * (A Shopify theme has no server-side wishlist without an app, so it does not follow the
 * shopper to another device.) Only product handles are stored; the wishlist page asks Shopify
 * to render each saved product through the Section Rendering API
 * (/products/<handle>?sections=wishlist-card), so prices and stock are always current.
 *
 * Hooks
 *   [data-wishlist-toggle][data-product-handle]   heart buttons (product cards, product page)
 *   [data-wishlist-remove][data-product-handle]   remove buttons on the wishlist page
 *   [data-wishlist-count]                          header / bottom-nav badges
 *   [data-wishlist-page]                           the page (snippets/wishlist-page.liquid)
 *
 * API: window.BEWishlist = { items, has, add, remove, toggle }
 */
(function () {
	'use strict';

	if (window.BEWishlist) return;

	var STORAGE_KEY = 'buyelectro:wishlist';
	var CARD_SECTION = 'wishlist-card';
	var CONCURRENCY = 4;
	var shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
	var wishlistUrl = shopRoot + 'pages/wishlist';

	var memoryCopy = null; // used when localStorage is unavailable (private mode, blocked storage)

	/* ───────────────────────── storage ───────────────────────── */

	function isItem(item) {
		return item && typeof item.handle === 'string' && item.handle.length > 0;
	}

	function read() {
		try {
			var raw = window.localStorage.getItem(STORAGE_KEY);
			var list = raw ? JSON.parse(raw) : [];
			return Array.isArray(list) ? list.filter(isItem) : [];
		} catch (error) {
			return (memoryCopy || []).slice();
		}
	}

	function write(list) {
		memoryCopy = list.slice();
		try {
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
		} catch (error) {
			/* storage blocked: the in-memory copy keeps this page working */
		}
		changed();
	}

	function items() {
		return read();
	}

	function has(handle) {
		return read().some(function (item) {
			return item.handle === handle;
		});
	}

	function add(handle, index) {
		if (!handle || has(handle)) return false;
		var list = read();
		var entry = { handle: handle, addedAt: Date.now() };
		if (typeof index === 'number' && index >= 0 && index <= list.length) {
			list.splice(index, 0, entry);
		} else {
			list.unshift(entry); // newest first
		}
		write(list);
		return true;
	}

	function remove(handle) {
		var list = read();
		var index = -1;
		list.forEach(function (item, i) {
			if (item.handle === handle) index = i;
		});
		if (index === -1) return -1;
		list.splice(index, 1);
		write(list);
		return index;
	}

	function toggle(handle) {
		if (has(handle)) {
			remove(handle);
			return false;
		}
		add(handle);
		return true;
	}

	/* ───────────────────────── hearts + badges ───────────────────────── */

	function paint(element, on) {
		element.setAttribute('aria-pressed', on ? 'true' : 'false');
		element.classList.toggle('is-active', on);

		var icon = element.querySelector('i.fa-heart');
		if (icon) {
			icon.classList.toggle('fa-solid', on);
			icon.classList.toggle('fa-regular', !on);
		}

		var label = element.querySelector('[data-wishlist-label]');
		if (label) {
			label.textContent = on
				? element.getAttribute('data-label-remove') || 'Remove from wishlist'
				: element.getAttribute('data-label-add') || 'Add to wishlist';
		}

		if (element.hasAttribute('title')) {
			element.setAttribute('title', on ? 'Remove from Wishlist' : 'Add to Wishlist');
		}
	}

	function sync() {
		var saved = {};
		var list = read();
		list.forEach(function (item) {
			saved[item.handle] = true;
		});

		document.querySelectorAll('[data-wishlist-toggle]').forEach(function (element) {
			paint(element, !!saved[element.getAttribute('data-product-handle')]);
		});
		document.querySelectorAll('[data-wishlist-count]').forEach(function (badge) {
			badge.textContent = list.length;
		});
	}

	function changed() {
		sync();
		renderPage();
		document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items: read() } }));
	}

	function toast(message, options) {
		if (window.BuyElectro && typeof window.BuyElectro.toast === 'function') {
			window.BuyElectro.toast(message, options);
		}
	}

	function onPage() {
		return !!document.querySelector('[data-wishlist-page]');
	}

	function removeWithUndo(handle) {
		var index = remove(handle);
		if (index === -1) return;
		toast('Removed from your wishlist', {
			duration: 6000, // long enough to actually reach the Undo button
			action: {
				label: 'Undo',
				onClick: function () {
					add(handle, index);
				},
			},
		});
	}

	/* ───────────────────────── wishlist page ───────────────────────── */

	var queue = [];
	var active = 0;

	function escapeValue(value) {
		return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');
	}

	function cardFor(grid, handle) {
		return grid.querySelector('[data-wishlist-card][data-product-handle="' + escapeValue(handle) + '"]');
	}

	function skeleton(handle) {
		var card = document.createElement('div');
		card.className = 'be-wishlist-card is-loading';
		card.setAttribute('data-wishlist-card', '');
		card.setAttribute('data-product-handle', handle);
		card.setAttribute('aria-busy', 'true');
		card.innerHTML =
			'<div class="be-wishlist-skeleton" aria-hidden="true">' +
			'<span class="be-wishlist-skeleton__media"></span>' +
			'<span class="be-wishlist-skeleton__line"></span>' +
			'<span class="be-wishlist-skeleton__line be-wishlist-skeleton__line--short"></span>' +
			'</div>';
		return card;
	}

	function messageCard(handle, text, buttonLabel, buttonAttribute) {
		var card = document.createElement('div');
		card.className = 'be-wishlist-card be-wishlist-card--message';
		card.setAttribute('data-wishlist-card', '');
		card.setAttribute('data-product-handle', handle);

		var box = document.createElement('div');
		box.className = 'be-wishlist-message';
		var p = document.createElement('p');
		p.textContent = text;
		var button = document.createElement('button');
		button.type = 'button';
		button.className = 'be-wl-btn be-wl-btn--ghost';
		button.setAttribute(buttonAttribute, '');
		button.setAttribute('data-product-handle', handle);
		button.textContent = buttonLabel;
		box.appendChild(p);
		box.appendChild(button);
		card.appendChild(box);
		return card;
	}

	function renderPage() {
		var root = document.querySelector('[data-wishlist-page]');
		if (!root) return;

		var grid = root.querySelector('[data-wishlist-grid]');
		var empty = root.querySelector('[data-wishlist-empty]');
		var count = root.querySelector('[data-wishlist-page-count]');
		var list = read();

		if (count) {
			count.textContent = list.length === 1 ? '1 item' : list.length + ' items';
			count.hidden = list.length === 0; // the empty state already says so
		}
		var clear = root.querySelector('[data-wishlist-clear]');
		if (clear) clear.hidden = list.length === 0;
		if (empty) empty.hidden = list.length > 0;
		grid.hidden = list.length === 0;
		root.setAttribute('data-ready', '');

		var wanted = {};
		list.forEach(function (item) {
			wanted[item.handle] = true;
		});

		Array.prototype.slice.call(grid.children).forEach(function (card) {
			if (!wanted[card.getAttribute('data-product-handle')]) card.remove();
		});

		list.forEach(function (item, index) {
			var card = cardFor(grid, item.handle);
			if (!card) {
				card = skeleton(item.handle);
				queue.push(item.handle);
			}
			if (grid.children[index] !== card) grid.insertBefore(card, grid.children[index] || null);
		});

		pump();
	}

	function pump() {
		while (active < CONCURRENCY && queue.length) {
			load(queue.shift());
		}
	}

	function load(handle) {
		active += 1;
		var url = shopRoot + 'products/' + encodeURIComponent(handle) + '?sections=' + CARD_SECTION;

		// No `Accept: application/json` header: on a product URL Shopify answers that with the
		// product's JSON and ignores ?sections= entirely.
		fetch(url)
			.then(function (response) {
				if (response.status === 404) return { missing: true };
				if (!response.ok) throw new Error('HTTP ' + response.status);
				return response.json().then(function (data) {
					return { html: data && data[CARD_SECTION] };
				});
			})
			.then(function (result) {
				place(handle, result);
			})
			.catch(function () {
				// Never drop a saved product because of a network hiccup — offer a retry.
				replaceCard(handle, messageCard(handle, 'We could not load this product.', 'Try again', 'data-wishlist-retry'));
			})
			.then(function () {
				active -= 1;
				pump();
			});
	}

	function place(handle, result) {
		var fresh = null;
		if (result.html) {
			var doc = new DOMParser().parseFromString(result.html, 'text/html');
			fresh = doc.querySelector('[data-wishlist-card]');
		}
		if (!fresh) {
			fresh = messageCard(handle, 'This product is no longer available.', 'Remove', 'data-wishlist-remove');
		}
		replaceCard(handle, fresh);
		startBuyBox(fresh);
		sync();
	}

	// snippets/inline-buy-box.liquid: variant choices + Add to cart / Buy now, run by
	// assets/product-buy-box.js, which only scans the page once at load.
	function startBuyBox(card) {
		if (!window.BEProductBuyBox || !card) return;
		var roots = card.matches('[data-product-root]') ? [card] : Array.prototype.slice.call(card.querySelectorAll('[data-product-root]'));
		roots.forEach(function (root) {
			root.removeAttribute('data-buy-box-ready');
			window.BEProductBuyBox.init(root);
		});
	}

	function replaceCard(handle, card) {
		var root = document.querySelector('[data-wishlist-page]');
		var grid = root && root.querySelector('[data-wishlist-grid]');
		var current = grid && cardFor(grid, handle);
		if (!current || !has(handle)) return; // removed while it was loading
		// the section may render the handle Shopify redirected to — keep the saved one
		card.setAttribute('data-product-handle', handle);
		grid.replaceChild(document.adoptNode(card), current);
	}

	/* ───────────────────────── events ───────────────────────── */

	document.addEventListener('click', function (event) {
		var target = event.target;
		if (!(target instanceof Element)) return;

		var heart = target.closest('[data-wishlist-toggle]');
		if (heart) {
			event.preventDefault();
			var handle = heart.getAttribute('data-product-handle');
			if (!handle) return;
			if (has(handle)) {
				removeWithUndo(handle);
			} else {
				add(handle);
				toast('Added to your wishlist', onPage() ? {} : { action: { label: 'View', href: wishlistUrl } });
			}
			return;
		}

		var clearButton = target.closest('[data-wishlist-clear]');
		if (clearButton) {
			event.preventDefault();
			var saved = read();
			if (!saved.length) return;
			write([]);
			toast('Wishlist cleared', {
				duration: 6000,
				action: {
					label: 'Undo',
					onClick: function () {
						write(saved);
					},
				},
			});
			return;
		}

		var removeButton = target.closest('[data-wishlist-remove]');
		if (removeButton) {
			event.preventDefault();
			removeWithUndo(removeButton.getAttribute('data-product-handle'));
			return;
		}

		var retry = target.closest('[data-wishlist-retry]');
		if (retry) {
			event.preventDefault();
			var retryHandle = retry.getAttribute('data-product-handle');
			var card = retry.closest('[data-wishlist-card]');
			if (card) card.replaceWith(skeleton(retryHandle));
			queue.push(retryHandle);
			pump();
		}
	});

	// Hearts on product cards are <span role="button">.
	document.addEventListener('keydown', function (event) {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		var target = event.target;
		if (!(target instanceof Element) || !target.matches('[data-wishlist-toggle][role="button"]')) return;
		event.preventDefault();
		target.click();
	});

	// Another tab changed the wishlist.
	window.addEventListener('storage', function (event) {
		if (event.key === STORAGE_KEY) {
			sync();
			renderPage();
		}
	});

	// Hearts that arrive later (wishlist cards, re-rendered sections) get painted too.
	var scheduled = false;
	function scheduleSync() {
		if (scheduled) return;
		scheduled = true;
		window.requestAnimationFrame(function () {
			scheduled = false;
			sync();
		});
	}

	function watch() {
		if (!window.MutationObserver) return;
		new MutationObserver(function (mutations) {
			for (var i = 0; i < mutations.length; i += 1) {
				var nodes = mutations[i].addedNodes;
				for (var j = 0; j < nodes.length; j += 1) {
					var node = nodes[j];
					if (
						node.nodeType === 1 &&
						(node.matches('[data-wishlist-toggle], [data-wishlist-count]') ||
							node.querySelector('[data-wishlist-toggle], [data-wishlist-count]'))
					) {
						scheduleSync();
						return;
					}
				}
			}
		}).observe(document.body, { childList: true, subtree: true });
	}

	function init() {
		sync();
		renderPage();
		watch();
	}

	window.BEWishlist = {
		items: items,
		has: has,
		add: add,
		remove: remove,
		toggle: toggle,
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
